import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, MessageSquareQuote, Sparkles } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { api, type ScheduledPost, type SurfacedTweet } from '@/lib/api'
import { useAuth } from '@/lib/auth-context'
import { formatRelative } from '@/lib/utils'

export default function Dashboard() {
  const { me } = useAuth()
  const [scheduled, setScheduled] = useState<ScheduledPost[]>([])
  const [engagement, setEngagement] = useState<SurfacedTweet[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([api.listScheduled(), api.listEngagement()])
      .then(([s, e]) => {
        setScheduled(s)
        setEngagement(e)
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
  }, [])

  const pendingDrafts = engagement.flatMap((t) => t.drafts.filter((d) => d.status === 'pending')).length
  const upcoming = scheduled.filter((s) => s.status === 'scheduled').slice(0, 5)
  const recentCaptures = engagement.slice(0, 5)

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Hey{me?.displayName ? `, ${me.displayName}` : ''}.</h1>
        <p className="text-muted-foreground">Here's what's moving today.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Stat icon={<Calendar className="h-4 w-4" />} label="Upcoming posts" value={upcoming.length} href="/app/schedule" />
        <Stat icon={<MessageSquareQuote className="h-4 w-4" />} label="Pending replies" value={pendingDrafts} href="/app/engagement" />
        <Stat icon={<Sparkles className="h-4 w-4" />} label="Plan" value={me?.plan ?? '—'} href="/app/settings" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Next up</CardTitle>
              <CardDescription>Scheduled to fire soon</CardDescription>
            </div>
            <Button asChild size="sm" variant="ghost"><Link to="/app/schedule">View all</Link></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && upcoming.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing scheduled yet.</p>
            )}
            {upcoming.map((s) => (
              <div key={s.id} className="rounded-md border bg-background px-3 py-2 text-sm">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="uppercase tracking-wide">{s.kind}</span>
                  <span>{formatRelative(s.fireAt)}</span>
                </div>
                <div className="mt-1 line-clamp-2">{s.tweets[0]}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Recent captures</CardTitle>
              <CardDescription>Tweets you've grabbed for engagement</CardDescription>
            </div>
            <Button asChild size="sm" variant="ghost"><Link to="/app/engagement">View all</Link></Button>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && recentCaptures.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing captured yet — install the bookmarklet from Settings.</p>
            )}
            {recentCaptures.map((t) => (
              <div key={t.id} className="rounded-md border bg-background px-3 py-2 text-sm">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>@{t.authorHandle}</span>
                  <span>{formatRelative(t.capturedAt)}</span>
                </div>
                <div className="mt-1 line-clamp-2">{t.tweetText}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Stat({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string | number; href: string }) {
  return (
    <Link to={href} className="rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </Link>
  )
}
